import { useEffect, useMemo, useState } from "react";
import { collection, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";
import {
  buildHistoryItems,
  fetchAskDiscoveryGrantItems,
  filterHiddenHistoryItems,
  filterHistoryItems,
  getHiddenHistoryItems,
  hideHistoryItem,
  hydrateFindGrantPreviews,
  restoreHiddenHistoryItem,
  sortHistoryItems,
  subscribeHiddenHistoryItemIds,
  subscribeUserHistoryItems,
} from "../services/historyService";
import { fetchTimeCapsulesByIds } from "../services/timeCapsuleService";
import { isSelfOnlyPrivateCapsule } from "../utils/messageClassification";
import { subscribeBoundedQuery } from "./subscribeBoundedQuery";

// Cap how many capsules each history source loads/live-syncs. Without this the
// recipient/claimed/sent listeners pulled the user's entire capsule history.
const HISTORY_CAPSULE_LIMIT = 200;

export default function useUserHistory(uid, { filter = "all", sort = "recent" } = {}) {
  const [explicitHistory, setExplicitHistory] = useState([]);
  const [hydratedExplicitFindHistory, setHydratedExplicitFindHistory] = useState([]);
  const [findGrants, setFindGrants] = useState([]);
  const [hydratedFindGrants, setHydratedFindGrants] = useState([]);
  const [recipientCapsules, setRecipientCapsules] = useState([]);
  const [claimedCapsules, setClaimedCapsules] = useState([]);
  const [sentPrivateCapsules, setSentPrivateCapsules] = useState([]);
  const [hiddenItemIds, setHiddenItemIds] = useState([]);
  const [loading, setLoading] = useState(Boolean(uid));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setExplicitHistory([]);
      setHydratedExplicitFindHistory([]);
      setFindGrants([]);
      setHydratedFindGrants([]);
      setRecipientCapsules([]);
      setClaimedCapsules([]);
      setSentPrivateCapsules([]);
      setHiddenItemIds([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    let pendingSnapshots = 2;
    setLoading(true);
    setError(null);

    const markSnapshotReady = () => {
      pendingSnapshots -= 1;
      if (active && pendingSnapshots <= 0) setLoading(false);
    };

    const handleError = (err) => {
      console.error("Failed to load history:", err);
      if (!active) return;
      setError(err);
      setLoading(false);
    };

    fetchAskDiscoveryGrantItems(uid)
      .then((items) => {
        if (active) setFindGrants(Array.isArray(items) ? items : []);
      })
      .catch(handleError);

    const unsubHistory = subscribeUserHistoryItems(
      uid,
      (items) => {
        setExplicitHistory(items);
        markSnapshotReady();
      },
      handleError
    );
    const unsubHidden = subscribeHiddenHistoryItemIds(
      uid,
      (ids) => {
        setHiddenItemIds(ids);
        markSnapshotReady();
      },
      handleError
    );
    const unsubRecipient = subscribeBoundedQuery(
      query(
        collection(db, "timeCapsules"),
        where("recipientUserIds", "array-contains", uid),
        orderBy("sentAt", "desc"),
        limit(HISTORY_CAPSULE_LIMIT)
      ),
      query(collection(db, "timeCapsules"), where("recipientUserIds", "array-contains", uid)),
      (items) => {
        setRecipientCapsules(items);
      },
      handleError
    );
    const unsubClaimed = subscribeBoundedQuery(
      query(
        collection(db, "timeCapsules"),
        where("claimedBy", "array-contains", uid),
        orderBy("sentAt", "desc"),
        limit(HISTORY_CAPSULE_LIMIT)
      ),
      query(collection(db, "timeCapsules"), where("claimedBy", "array-contains", uid)),
      (items) => {
        setClaimedCapsules(items);
      },
      handleError
    );
    const unsubSentPrivate = subscribeBoundedQuery(
      query(
        collection(db, "timeCapsules"),
        where("senderId", "==", uid),
        where("capsuleType", "==", "private"),
        orderBy("createdAt", "desc"),
        limit(HISTORY_CAPSULE_LIMIT)
      ),
      query(collection(db, "timeCapsules"), where("senderId", "==", uid), where("capsuleType", "==", "private")),
      (items) => {
        setSentPrivateCapsules(items);
      },
      handleError
    );

    return () => {
      active = false;
      unsubHistory?.();
      unsubHidden?.();
      unsubRecipient?.();
      unsubClaimed?.();
      unsubSentPrivate?.();
    };
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setHydratedExplicitFindHistory([]);
      setHydratedFindGrants([]);
      return undefined;
    }

    const explicitFindItems = explicitHistory.filter(
      (item) => item?.type === "find"
    );
    const allFindItems = [...explicitFindItems, ...findGrants];
    if (!allFindItems.length) {
      setHydratedExplicitFindHistory([]);
      setHydratedFindGrants([]);
      return undefined;
    }

    let active = true;
    const previewCapsuleIds = Array.from(
      new Set(
        allFindItems
          .flatMap((item) => (Array.isArray(item?.capsuleIds) ? item.capsuleIds : [item?.capsuleId]))
          .map((id) => String(id || "").trim())
          .filter(Boolean)
      )
    );

    if (!previewCapsuleIds.length) {
      setHydratedExplicitFindHistory([]);
      setHydratedFindGrants([]);
      return undefined;
    }

    fetchTimeCapsulesByIds(previewCapsuleIds, uid)
      .then((previewCapsules) => {
        if (!active) return;
        setHydratedExplicitFindHistory(hydrateFindGrantPreviews(explicitFindItems, previewCapsules));
        setHydratedFindGrants(hydrateFindGrantPreviews(findGrants, previewCapsules));
      })
      .catch((err) => {
        console.warn("Failed to hydrate Find history previews:", err);
        if (active) {
          setHydratedExplicitFindHistory([]);
          setHydratedFindGrants([]);
        }
      });

    return () => {
      active = false;
    };
  }, [explicitHistory, findGrants, uid]);

  const nonFindExplicitHistory = useMemo(
    () => explicitHistory.filter((item) => item?.type !== "find"),
    [explicitHistory]
  );

  const mergedExplicitHistory = useMemo(
    () => [...nonFindExplicitHistory, ...hydratedExplicitFindHistory],
    [hydratedExplicitFindHistory, nonFindExplicitHistory]
  );

  const receivedCapsules = useMemo(() => {
    const byId = new Map();
    [...recipientCapsules, ...claimedCapsules].forEach((capsule) => {
      if (!capsule?.id) return;
      if (isSelfOnlyPrivateCapsule(capsule, uid)) return;
      byId.set(capsule.id, capsule);
    });
    return Array.from(byId.values());
  }, [claimedCapsules, recipientCapsules, uid]);

  const personalCapsules = useMemo(
    () => sentPrivateCapsules.filter((capsule) => isSelfOnlyPrivateCapsule(capsule, uid)),
    [sentPrivateCapsules, uid]
  );

  const mergedItems = useMemo(
    () =>
      buildHistoryItems({
        uid,
        findGrants: hydratedFindGrants,
        explicitHistory: mergedExplicitHistory,
        receivedCapsules,
        personalCapsules,
      }),
    [hydratedFindGrants, mergedExplicitHistory, personalCapsules, receivedCapsules, uid]
  );

  const allItems = useMemo(
    () => filterHiddenHistoryItems(mergedItems, hiddenItemIds),
    [hiddenItemIds, mergedItems]
  );

  const hiddenItems = useMemo(
    () => sortHistoryItems(getHiddenHistoryItems(mergedItems, hiddenItemIds), sort),
    [hiddenItemIds, mergedItems, sort]
  );

  const items = useMemo(
    () => sortHistoryItems(filterHistoryItems(allItems, filter), sort),
    [allItems, filter, sort]
  );

  const summary = useMemo(
    () => ({
      capsules: allItems.filter((item) => item.type !== "find").length,
      finds: allItems.filter((item) => item.filterType === "find").length,
      received: allItems.filter((item) => item.filterType === "received").length,
    }),
    [allItems]
  );

  return {
    items,
    allItems,
    hiddenItems,
    hiddenCount: hiddenItems.length,
    summary,
    loading,
    error,
    hideHistoryItem: (itemId) => hideHistoryItem(uid, itemId),
    restoreHistoryItem: (itemId) => restoreHiddenHistoryItem(uid, itemId),
  };
}
